import { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { CustomDataTable } from 'components/DatatableComponent';
import { AddCircleOutlineOutlined, HighlightOffRounded, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { renderSelectEditInputCell } from 'pages/SystemTenant/AROKMS/TableDefinition/components/SelectEditInputCell';
import { useGetFunctionDefinitionSubjectAttributeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { GridRowOrderChangeParams } from '@mui/x-data-grid-pro';

export interface ScoreMapperRow {
  id: string;
  sourceTableDefinition: AutoCompleteItem | null;
  condition: AutoCompleteItem | null;
  ruleValue: string;
  priorityOrder: number;
}

interface ScoreMapperTableProps {
  onDataChange: (data: ScoreMapperRow[]) => void;
  initialData?: ScoreMapperRow[];
  initialSubjectSource?: AutoCompleteItem | null;
}

const CONDITION_OPTIONS = [
  { label: 'Greater Than', value: 'GREATER_THAN' },
  { label: 'Less Than', value: 'LESS_THAN' },
  { label: 'Equal', value: 'EQUAL' },
  { label: 'Not Equal', value: 'NOT_EQUAL' },
];

export function ScoreMapperTable({
  onDataChange,
  initialData = [],
  initialSubjectSource = null,
}: ScoreMapperTableProps) {
  const [rows, setRows] = useState<ScoreMapperRow[]>(initialData);
  const [rowModesModel, setRowModesModel] = useState<Record<string, any>>({});
  const [subjectSource, setSubjectSource] = useState<AutoCompleteItem | null>(initialSubjectSource);
  // Fetch attributes based on the selected subject source
  const { data: attributeDataDropdown } = useGetFunctionDefinitionSubjectAttributeDropdown(
    subjectSource?.value?.toString() ?? null
  );

  const handleAddRow = () => {
    const newId = `id_${rows.length + 1}`;
    const newRow: ScoreMapperRow = {
      id: newId,
      sourceTableDefinition: null,
      condition: null,
      ruleValue: '',
      priorityOrder: rows.length,
    };
    const newRows = [...rows, newRow];
    setRows(newRows);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: 'edit', fieldToFocus: 'sourceTableDefinition' } }));
    onDataChange(newRows);
  };

  const handleDeleteRow = (id: string) => {
    const newRows = rows.filter((row) => row.id !== id);
    setRows(newRows);
    setRowModesModel((prev) => {
      const newModes = { ...prev };
      delete newModes[id];
      return newModes;
    });
    onDataChange(newRows);
  };

  const handleEditRow = (id: string) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: 'edit', fieldToFocus: 'sourceTableDefinition' } }));
  };

  const handleSaveRow = (id: string) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: 'view' } }));
  };

  const isAnyEditing = Object.values(rowModesModel).some((mode) => mode.mode === 'edit');

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    const { oldIndex, targetIndex } = params;
    if (oldIndex === targetIndex) return;

    setRows((prevRows) => {
      const newRows = [...prevRows];
      const [movedRow] = newRows.splice(oldIndex, 1);
      newRows.splice(targetIndex, 0, movedRow);
      return newRows.map((row, index) => ({ ...row, priorityOrder: index }));
    });
  };

  const columns = [
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const isEditing = rowModesModel[params.row.id]?.mode === 'edit';
        return (
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <IconButton
              onClick={() => handleDeleteRow(params.row.id)}
              size='small'
              disabled={isAnyEditing && !isEditing}
            >
              <HighlightOffRounded fontSize='small' />
            </IconButton>
            {isEditing ? (
              <IconButton onClick={() => handleSaveRow(params.row.id)} size='small'>
                <SaveIcon fontSize='small' />
              </IconButton>
            ) : (
              <IconButton onClick={() => handleEditRow(params.row.id)} size='small' disabled={isAnyEditing}>
                <EditIcon fontSize='small' />
              </IconButton>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'priorityOrder',
      headerName: 'Priority',
      width: 80,
      editable: false,
      renderCell: (params: any) => {
        return <span>{params.value + 1}</span>;
      },
    },
    {
      field: 'condition',
      headerName: 'Condition',
      width: 160,
      editable: true,
      renderEditCell: (params: any) =>
        renderSelectEditInputCell({
          ...params,
          optionsSource: CONDITION_OPTIONS,
        }),
      renderCell: (params: any) => (params.value ? params.value.label : ''),
    },
    {
      field: 'sourceTableDefinition',
      headerName: 'Source Table Definition',
      width: 200,
      editable: true,
      renderEditCell: (params: any) =>
        renderSelectEditInputCell({
          ...params,
          optionsSource: attributeDataDropdown?.data,
        }),
      renderCell: (params: any) => (params.value ? params.value.label : ''),
    },
    {
      field: 'ruleValue',
      headerName: 'Rule Value (Score)',
      width: 150,
      editable: true,
      renderCell: (params: any) => params.value || '',
    },
  ];

  useEffect(() => {
    if (initialData.length > 0) {
      const newRows = initialData.map((row, index) => ({
        ...row,
        priorityOrder: index,
        id: `id_${index + 1}`,
      }));
      setRows(newRows);
    }
  }, [initialData]);
  useEffect(() => {
    if (initialSubjectSource) {
      setSubjectSource(initialSubjectSource);
    }
  }, [initialSubjectSource]);

  return (
    <>
      <Typography variant='body2' fontWeight='bold' color='black' mb={1}>
        Score Mapping Rules
      </Typography>
      <Stack width='25%' mb={2}>
        <Button variant='main-table-panel' startIcon={<AddCircleOutlineOutlined />} onClick={handleAddRow}>
          Add Rule
        </Button>
      </Stack>

      <CustomDataTable
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        disableSelectionOnClick
        sortingMode='client'
        filterMode='client'
        paginationMode='client'
        density='compact'
        rowHeight={45}
        autoHeight
        sx={{ backgroundColor: '#fff', minHeight: 350 }}
        getRowId={(row) => row.id}
        isCellEditable={() => true}
        components={{ Toolbar: null, Pagination: null }}
        experimentalFeatures={{ newEditingApi: true }}
        onRowEditStop={(params, event) => (event.defaultMuiPrevented = true)}
        processRowUpdate={(newRow) => {
          const updatedRows = rows.map((row) => (row.id === newRow.id ? newRow : row));
          setRows(updatedRows);
          onDataChange(updatedRows);
          return newRow;
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        rowReordering
        onRowOrderChange={handleRowOrderChange}
      />
    </>
  );
}
